const internals = {};

const Dayjs = require('dayjs');

module.exports.buildExcludedUserIds = function buildExcludedUserIds(searchResults = [], selectedUsers = []) {

    const selectedIds = new Set((selectedUsers || []).map((user) => Number(user.id ?? user)));

    return [...new Set(searchResults
        .map((user) => Number(user.id ?? user))
        .filter((id) => Number.isInteger(id) && id > 0 && !selectedIds.has(id)))];
};

module.exports.buildFormConfig = function buildFormConfig(props) {

    const {
        formId,
        onSubmit
    } = props;

    return {
        formId,
        fields: internals.buildFields(props),
        onSubmit,
        syncDefaults: false
    };
};

internals.buildRoleOptions = (schoolRoles = []) => ([
    { value: '', label: 'Select...' },
    { value: 'all', label: 'All Users' },
    { value: 'search', label: 'Search-based' },
    ...schoolRoles.map((schoolRole) => ({
        value: schoolRole.id,
        label: `Role: ${schoolRole.label}`
    })),
    { value: 'test-notify', label: 'Test: Notify Me' },
    { value: 'csv', label: 'CSV-based' }
]);

internals.buildRawFields = ({
    SelectedUsersField,
    dropdownUsersOpen,
    onDropdownToggle,
    onRoleChange,
    onStartTimeOpen,
    renderNotificationMessage,
    renderActiveFilters,
    schoolName,
    schoolRoles,
    searchResults,
    selectedRoleId,
    validateCsv,
} = {}) => [
    {
        type: 'EmojiField',
        name: 'emojiSymbol',
        label: 'Notification Emoji',
        default: ''
    },
    {
        type: 'SelectField',
        name: 'selectedRoleId',
        label: 'Role to Notify',
        required: true,
        default: '',
        options: internals.buildRoleOptions(schoolRoles),
        onChange: onRoleChange,
        textFieldProps: {
            variant: 'standard'
        }
    },
    selectedRoleId === 'search' && {
        type: 'custom',
        key: 'active-search-filters',
        render: renderActiveFilters
    },
    selectedRoleId === 'search' && {
        name: 'selectedUsers',
        component: SelectedUsersField,
        default: searchResults || [],
        componentProps: {
            options: searchResults || [],
            dropdownUsersOpen,
            onDropdownToggle
        }
    },
    selectedRoleId === 'csv' && {
        type: 'FileField',
        name: 'csv',
        default: null,
        fullWidth: true,
        label: 'CSV file required',
        description: `Upload a CSV file containing user emails to notify. Make sure the file contains only one column, with one header row named 'email', followed by one row per user email. Please enter the notification message in the field below.`,
        contextLabel: `For ${schoolName}`,
        minHeight: 112,
        showActionButton: true,
        accept: 'text/csv',
        rules: {
            validate: validateCsv
        }
    },
    {
        type: 'CheckboxField',
        name: 'usePushSystem',
        label: 'Send push notification',
        default: true
    },
    {
        type: 'DateTimeField',
        name: 'startTime',
        label: `Schedule Notification for local time (${Dayjs.tz.guess()})`,
        default: null,
        pickerProps: {
            onOpen: onStartTimeOpen,
            minDateTime: Dayjs(),
            disablePast: true,
            closeOnSelect: false,
            minutesStep: 10,
            shouldDisableTime: (value, view) => view === 'minutes' && value.minute() % 10 !== 0,
            views: ['year', 'month', 'day', 'hours', 'minutes']
        },
        textFieldProps: {
            variant: 'standard',
            helperText: 'Click on calendar icon to set the time or leave blank to send immediately'
        }
    },
    {
        type: 'custom',
        key: 'notification-message',
        fullWidth: true,
        render: renderNotificationMessage
    }
];

internals.buildFields = (props = {}) => {

    const fields = internals.buildRawFields(props);

    return (fields || [])
        .filter((field) => !!field)
        .map(internals.normalizeField)
        .reduce((collector, field, index, normalizedFields) => {

            collector.push(field);

            if (internals.shouldBreakAfter(field, normalizedFields[index + 1])) {
                collector.push({
                    type: 'break',
                    key: `${field.name || field.key || index}-break`
                });
            }

            return collector;
        }, []);
};

internals.normalizeField = (field) => {

    if (!field || typeof field !== 'object') {
        return field;
    }

    if (Array.isArray(field.fields)) {
        return {
            ...field,
            fields: field.fields
                .filter((item) => !!item)
                .map(internals.normalizeField)
        };
    }

    const nextField = { ...field };

    if (nextField.name === 'selectedRoleId' || nextField.name === 'startTime') {
        const {
            variant,
            ...textFieldProps
        } = nextField.textFieldProps || {};

        nextField.textFieldProps = textFieldProps;
    }

    if (nextField.name === 'usePushSystem' || nextField.name === 'startTime') {
        nextField.fullWidth = true;
    }

    return nextField;
};

internals.shouldBreakAfter = (field, nextField) => {

    if (!field || !nextField) {
        return false;
    }

    return field.name === 'selectedRoleId';
};

module.exports.internals = internals;
